import {useEffect, useMemo, useState} from "react";
import {Package, Plus, Search, Trash2} from "lucide-react";
import {adminSuggest, type AdminOrderDetail, type AdminOrderLine, type AdminSuggestItem} from "@zaaaz/shared";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/ui/table";
import {OrderSection} from "./OrderSection";

type DraftLine = {
  productId: number;
  name: string;
  code: string;
  brandName: string;
  stock: number;
  quantity: number;
  unitPrice: number;
};

type Props = {
  detail: AdminOrderDetail;
  onSaveLines: (id: number, lines: {productId: number; quantity: number}[]) => Promise<void>;
};

function toDraft(lines: AdminOrderLine[]): DraftLine[] {
  return lines.map((l) => ({
    productId: l.productId,
    name: l.name,
    code: l.code,
    brandName: l.brandName || "",
    stock: l.stock ?? 0,
    quantity: l.quantity,
    unitPrice: l.unitPrice
  }));
}

export function OrderProductsTab({detail, onSaveLines}: Props) {
  const locked = detail.status === 4;
  const [draft, setDraft] = useState<DraftLine[]>(() => toDraft(detail.lines));
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState<AdminSuggestItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    setDraft(toDraft(detail.lines));
  }, [detail.id, detail.lines]);

  useEffect(() => {
    if(locked) return;
    const term = q.trim();
    if(term.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = window.setTimeout(() => {
      adminSuggest(term).then((r: {items: AdminSuggestItem[]}) => {
        if(!cancelled) setSuggestions(r.items || []);
      }).catch(() => {
        if(!cancelled) setSuggestions([]);
      }).finally(() => {
        if(!cancelled) setSearching(false);
      });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [q, locked]);

  const dirty = useMemo(() => {
    const a = detail.lines.map((l) => `${l.productId}:${l.quantity}`).join("|");
    const b = draft.map((l) => `${l.productId}:${l.quantity}`).join("|");
    return a !== b;
  }, [detail.lines, draft]);

  const draftTotal = draft.reduce((s, l) => s + l.unitPrice * l.quantity, 0);

  function setQty(productId: number, quantity: number) {
    setDraft((rows) => rows.map((r) => r.productId === productId ? {...r, quantity: Math.max(1, quantity)} : r));
  }

  function removeLine(productId: number) {
    setDraft((rows) => rows.filter((r) => r.productId !== productId));
  }

  function addProduct(item: AdminSuggestItem) {
    setDraft((rows) => {
      const existing = rows.find((r) => r.productId === item.id);
      if(existing) {
        return rows.map((r) => r.productId === item.id ? {...r, quantity: r.quantity + 1} : r);
      }
      return [...rows, {
        productId: item.id,
        name: item.nume,
        code: item.cod,
        brandName: item.brandName || "",
        stock: item.stoc,
        quantity: 1,
        unitPrice: item.pret
      }];
    });
    setQ("");
    setSuggestions([]);
  }

  async function save() {
    if(draft.length === 0) return;
    setSaving(true);
    try {
      await onSaveLines(detail.id, draft.map((l) => ({productId: l.productId, quantity: l.quantity})));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {!locked ? (
        <OrderSection title="Add product" icon={<Search />} dense>
          <div className="relative">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or code…"
              autoComplete="off"
              className="bg-background"
            />
            {(suggestions.length > 0 || searching) && q.trim().length >= 2 ? (
              <div className="absolute z-20 mt-1.5 max-h-56 w-full overflow-auto rounded-xl border border-border/70 bg-card shadow-lg">
                {searching && !suggestions.length ? (
                  <p className="px-3 py-2.5 text-sm text-muted-foreground">Searching…</p>
                ) : null}
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="flex w-full items-start justify-between gap-2 border-b border-border/40 px-3 py-2.5 text-left text-sm last:border-0 hover:bg-muted/50"
                    onClick={() => addProduct(s)}
                  >
                    <span>
                      {s.brandName ? <span className="mb-0.5 block text-xs font-bold">{s.brandName}</span> : null}
                      <span className="font-medium">{s.nume}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{s.cod} · stock {s.stoc}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">{s.pret.toFixed(2)} lei</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </OrderSection>
      ) : (
        <OrderSection title="Products" icon={<Package />}>
          <p className="text-sm text-muted-foreground">Cancelled order — products locked.</p>
        </OrderSection>
      )}

      <OrderSection
        title="Line items"
        icon={<Package />}
        description={`${draft.length} product${draft.length === 1 ? "" : "s"}`}
        dense
      >
        <div className="-mx-1 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-9">Product</TableHead>
                <TableHead className="h-9 w-28">Qty</TableHead>
                <TableHead className="h-9 w-28 text-right">Unit</TableHead>
                <TableHead className="h-9 w-28 text-right">Total</TableHead>
                {!locked ? <TableHead className="h-9 w-12" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {draft.map((l) => (
                <TableRow key={l.productId}>
                  <TableCell>
                    {l.brandName ? (
                      <p className="text-sm font-bold text-foreground">{l.brandName}</p>
                    ) : null}
                    <p className="font-medium">{l.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Stock: {l.stock}
                      {l.code ? ` · ${l.code}` : ""}
                      {` · #${l.productId}`}
                    </p>
                  </TableCell>
                  <TableCell>
                    {locked ? (
                      <span className="tabular-nums">{l.quantity}</span>
                    ) : (
                      <Input
                        type="number"
                        min={1}
                        className="h-8 w-20 bg-background"
                        value={l.quantity}
                        onChange={(e) => setQty(l.productId, parseInt(e.target.value, 10) || 1)}
                      />
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{l.unitPrice.toFixed(2)}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{(l.unitPrice * l.quantity).toFixed(2)}</TableCell>
                  {!locked ? (
                    <TableCell>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeLine(l.productId)} aria-label="Remove">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-4">
          <p className="text-sm text-muted-foreground">
            Lines subtotal (est.):{" "}
            <span className="font-semibold tabular-nums text-foreground">{draftTotal.toFixed(2)} lei</span>
          </p>
          {!locked ? (
            <Button type="button" disabled={!dirty || draft.length === 0 || saving} onClick={save}>
              <Plus className="mr-1 h-4 w-4" />
              {saving ? "Saving…" : "Save products"}
            </Button>
          ) : null}
        </div>
      </OrderSection>
    </div>
  );
}
